import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  GradingRules,
  GradeScale,
  GradeScaleType,
  DEFAULT_GRADING_RULES,
  GRADE_SCALE_PRESETS,
  DEFAULT_THRESHOLDS_BY_SCALE,
  GradeScaleUtils,
} from '@/shared/domain/entities/Workspace';

/**
 * Workspace Settings Store State Interface
 * Manages global state for workspace settings including grading rules
 */
interface WorkspaceSettingsState {
  // Data
  workspaceId: number | null;
  gradingRules: GradingRules;
  allowLessonDownloads: boolean;
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;

  // Actions - Setters
  setWorkspaceId: (workspaceId: number) => void;
  setGradingRules: (rules: Partial<GradingRules>) => void;
  setScale: (scaleType: GradeScaleType, updateThresholds?: boolean) => void;
  setAllowLessonDownloads: (allow: boolean) => void;

  // Actions - State Management
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Actions - Helpers
  getScale: () => GradeScale;
  getPassingGrade: () => number;
  getLevelUnlockGrade: () => number;
  isPassing: (grade: number | null) => boolean;
  canUnlockLevel: (grade: number | null) => boolean;
  formatGrade: (grade: number | null) => string;
  formatGradeValue: (grade: number | null) => string;
  isValidGrade: (grade: number) => boolean;
  getValidationError: (grade: number) => string | null;
  toPercentage: (grade: number) => number;
  reset: () => void;
}

/**
 * Initial state for workspace settings store
 */
const initialState = {
  workspaceId: null,
  gradingRules: DEFAULT_GRADING_RULES,
  allowLessonDownloads: false,
  isLoading: false,
  error: null,
  lastFetched: null,
};

/**
 * Workspace Settings Store
 *
 * Global state management for workspace settings using Zustand.
 * Features:
 * - Persistent storage (localStorage)
 * - Configurable grading rules
 * - Helper methods for grade checks
 *
 * Usage:
 * ```typescript
 * const { gradingRules, isPassing, setGradingRules } = useWorkspaceSettingsStore();
 *
 * // Check if a grade is passing
 * const passed = isPassing(75); // true if >= passingGrade
 *
 * // Update grading rules
 * setGradingRules({ passingGrade: 65 });
 * ```
 */
export const useWorkspaceSettingsStore = create<WorkspaceSettingsState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Setters
      setWorkspaceId: (workspaceId) =>
        set({
          workspaceId,
          lastFetched: Date.now(),
        }),

      setGradingRules: (rules) =>
        set((state) => ({
          gradingRules: {
            ...state.gradingRules,
            ...rules,
          },
          lastFetched: Date.now(),
          error: null,
        })),

      setScale: (scaleType, updateThresholds = true) =>
        set((state) => {
          const newScale = GRADE_SCALE_PRESETS[scaleType];
          const thresholds = updateThresholds
            ? DEFAULT_THRESHOLDS_BY_SCALE[scaleType]
            : { passing: state.gradingRules.passingGrade, unlock: state.gradingRules.levelUnlockGrade };

          return {
            gradingRules: {
              scale: newScale,
              passingGrade: thresholds.passing,
              levelUnlockGrade: thresholds.unlock,
            },
            lastFetched: Date.now(),
            error: null,
          };
        }),

      setAllowLessonDownloads: (allow) =>
        set({
          allowLessonDownloads: allow,
          lastFetched: Date.now(),
          error: null,
        }),

      // State Management
      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error, isLoading: false }),

      // Helpers
      getScale: () => {
        const state = get();
        return state.gradingRules.scale;
      },

      getPassingGrade: () => {
        const state = get();
        return state.gradingRules.passingGrade;
      },

      getLevelUnlockGrade: () => {
        const state = get();
        return state.gradingRules.levelUnlockGrade;
      },

      isPassing: (grade) => {
        if (grade === null) return false;
        const state = get();
        return grade >= state.gradingRules.passingGrade;
      },

      canUnlockLevel: (grade) => {
        if (grade === null) return false;
        const state = get();
        return grade >= state.gradingRules.levelUnlockGrade;
      },

      formatGrade: (grade) => {
        const state = get();
        return GradeScaleUtils.formatGrade(grade, state.gradingRules.scale);
      },

      formatGradeValue: (grade) => {
        const state = get();
        return GradeScaleUtils.formatGradeValue(grade, state.gradingRules.scale);
      },

      isValidGrade: (grade) => {
        const state = get();
        return GradeScaleUtils.isValidGrade(grade, state.gradingRules.scale);
      },

      getValidationError: (grade) => {
        const state = get();
        return GradeScaleUtils.getValidationError(grade, state.gradingRules.scale);
      },

      toPercentage: (grade) => {
        const state = get();
        return GradeScaleUtils.toPercentage(grade, state.gradingRules.scale);
      },

      reset: () => set(initialState),
    }),
    {
      name: 'workspace-settings-storage', // localStorage key
      storage: createJSONStorage(() => localStorage),
      // Only persist data, not loading/error states
      partialize: (state) => ({
        workspaceId: state.workspaceId,
        gradingRules: state.gradingRules,
        allowLessonDownloads: state.allowLessonDownloads,
        lastFetched: state.lastFetched,
      }),
    }
  )
);

/**
 * Selectors for optimized component rendering
 */
export const workspaceSettingsSelectors = {
  selectGradingRules: (state: WorkspaceSettingsState) => state.gradingRules,
  selectScale: (state: WorkspaceSettingsState) => state.gradingRules.scale,
  selectScaleType: (state: WorkspaceSettingsState) => state.gradingRules.scale.type,
  selectScaleMax: (state: WorkspaceSettingsState) => state.gradingRules.scale.max,
  selectPassingGrade: (state: WorkspaceSettingsState) => state.gradingRules.passingGrade,
  selectLevelUnlockGrade: (state: WorkspaceSettingsState) => state.gradingRules.levelUnlockGrade,
  selectAllowLessonDownloads: (state: WorkspaceSettingsState) => state.allowLessonDownloads,
  selectIsLoading: (state: WorkspaceSettingsState) => state.isLoading,
  selectError: (state: WorkspaceSettingsState) => state.error,
};

/**
 * Export scale presets for UI components
 */
export { GRADE_SCALE_PRESETS, DEFAULT_THRESHOLDS_BY_SCALE, GradeScaleUtils };
export type { GradeScale, GradeScaleType };
